export interface NHSServiceFAQ {
  question: string;
  answer: string;
}

export interface NHSService {
  id: string;
  title: string;
  category: string;
  shortDescription: string;
  fullDescription: string;
  seoTitle: string;
  metaDescription: string;
  whoIsFor: string[];
  faqs: NHSServiceFAQ[];
  bookingUrl: string;
}

export interface NHSServiceCategory {
  id: string;
  label: string;
  icon: string;
  description: string;
}

export const nhsServiceCategories: NHSServiceCategory[] = [
  {
    id: 'vaccination',
    label: 'NHS Vaccination Services',
    icon: '💉',
    description: 'Free NHS vaccinations to protect against seasonal illnesses',
  },
  {
    id: 'health-checks',
    label: 'NHS Health Checks & Screening',
    icon: '🩺',
    description: 'Free health monitoring and early detection services',
  },
  {
    id: 'medicines-support',
    label: 'NHS Medicines Support',
    icon: '💊',
    description: 'Support with new and ongoing medications',
  },
  {
    id: 'womens-health',
    label: "NHS Women's Health & Contraception",
    icon: '🌸',
    description: 'Confidential contraception and reproductive health services',
  },
  {
    id: 'pharmacy-first',
    label: 'NHS Pharmacy First',
    icon: '⚕️',
    description: 'Treatment for common conditions without a GP appointment',
  },
];

export const nhsServices: NHSService[] = [
  // Vaccination Services
  {
    id: 'adult-flu-vaccination',
    title: 'Adult Flu Vaccination Service',
    category: 'vaccination',
    shortDescription: 'Free NHS flu jabs for eligible adults to protect against seasonal influenza.',
    fullDescription: 'Wilberforce Pharmacy provides the NHS Adult Flu Vaccination Service, helping protect eligible patients against seasonal influenza. The flu virus changes each year, so annual vaccination is the best way to reduce the risk of serious illness and complications.',
    seoTitle: 'Free NHS Flu Vaccination in Hull | Wilberforce Pharmacy',
    metaDescription: 'Get your free NHS flu jab at Wilberforce Pharmacy Hull. No GP appointment needed. Eligible adults protected against seasonal influenza.',
    whoIsFor: [
      'Adults aged 65 or over',
      'People with long-term health conditions',
      'Pregnant women',
      'Carers',
      'Frontline health or social care workers',
    ],
    faqs: [
      {
        question: 'Is the flu vaccine free?',
        answer: 'Yes, if you meet NHS eligibility criteria.',
      },
      {
        question: 'Do I need a GP appointment?',
        answer: 'No. You can receive your NHS flu jab at Wilberforce Pharmacy.',
      },
      {
        question: 'Can I get side effects?',
        answer: 'Most people only experience mild effects such as a sore arm.',
      },
      {
        question: 'When should I get vaccinated?',
        answer: 'The best time is in autumn before flu starts circulating, typically between September and November.',
      },
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/services/695606c5-0a2e-4986-b4c9-bfaaa597dea0',
  },
  {
    id: 'covid-vaccination',
    title: 'COVID-19 Vaccination Service',
    category: 'vaccination',
    shortDescription: 'NHS COVID-19 vaccinations and seasonal boosters for eligible patients.',
    fullDescription: 'Wilberforce Pharmacy delivers the NHS COVID-19 Vaccination Service, including seasonal boosters for eligible patients, helping protect against serious illness from COVID-19.',
    seoTitle: 'NHS COVID-19 Vaccination in Hull | Wilberforce Pharmacy',
    metaDescription: 'Get your NHS COVID-19 vaccine or booster at Wilberforce Pharmacy Hull. Free for eligible patients.',
    whoIsFor: [
      'Older adults',
      'People with weakened immune systems',
      'Those with specific long-term conditions',
      'Care home residents and carers',
    ],
    faqs: [
      {
        question: 'Is the COVID vaccine free?',
        answer: 'Yes, for eligible patients under the NHS programme.',
      },
      {
        question: 'Can I get the COVID vaccine with my flu jab?',
        answer: 'Yes, where clinically appropriate.',
      },
      {
        question: 'Do I need proof?',
        answer: 'Vaccination records are updated on NHS systems.',
      },
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
  },

  // Health Checks & Screening
  {
    id: 'hypertension-case-finding',
    title: 'Hypertension Case-Finding Service',
    category: 'health-checks',
    shortDescription: 'Free NHS blood pressure checks to help identify high blood pressure early.',
    fullDescription: 'This NHS service helps identify high blood pressure (hypertension) early. Wilberforce Pharmacy offers free blood pressure checks and, where needed, follow-up monitoring. Early detection reduces the risk of heart disease and stroke.',
    seoTitle: 'Free Blood Pressure Check in Hull | NHS Pharmacy Service',
    metaDescription: 'NHS blood pressure checks in Hull at Wilberforce Pharmacy. Early detection of high blood pressure without GP appointment.',
    whoIsFor: [
      'Adults aged 40+ with no recent BP check',
      'People with previously raised readings',
      'Those referred by their GP',
    ],
    faqs: [
      {
        question: 'Is this service free?',
        answer: "Yes, it's fully NHS-funded.",
      },
      {
        question: 'What happens if my BP is high?',
        answer: 'You may be offered home or ambulatory monitoring or referred to your GP.',
      },
      {
        question: 'Do I need symptoms?',
        answer: 'No. High blood pressure often has no symptoms.',
      },
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
  },
  {
    id: 'lateral-flow-device',
    title: 'Lateral Flow Device (LFD) Service',
    category: 'health-checks',
    shortDescription: 'Access to NHS-funded lateral flow test kits for eligible patients.',
    fullDescription: 'Wilberforce Pharmacy supplies NHS-funded lateral flow test kits to eligible patients, supporting infection control and public health guidance.',
    seoTitle: 'NHS Lateral Flow Tests in Hull | Wilberforce Pharmacy',
    metaDescription: 'Get NHS-funded lateral flow test kits at Wilberforce Pharmacy Hull for eligible patients.',
    whoIsFor: [
      'Certain health or care settings',
      'Patients at higher clinical risk',
    ],
    faqs: [
      {
        question: 'Are LFD tests free?',
        answer: 'Yes, for eligible groups under NHS rules.',
      },
      {
        question: 'Do I need ID?',
        answer: 'Eligibility requirements may apply—ask in pharmacy.',
      },
      {
        question: 'Are these tests accurate?',
        answer: 'They are designed for rapid screening and should be used as instructed.',
      },
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
  },

  // Medicines Support
  {
    id: 'new-medicine-service',
    title: 'New Medicine Service (NMS)',
    category: 'medicines-support',
    shortDescription: 'Free follow-up support if you have recently started a new long-term medicine.',
    fullDescription: 'The NHS New Medicine Service provides free support to patients who have recently started certain new long-term medicines, helping improve understanding, safety and adherence.',
    seoTitle: 'New Medicine Service in Hull | NHS Pharmacy Support',
    metaDescription: 'Free NHS support for new medicines at Wilberforce Pharmacy Hull. Get help understanding your medication.',
    whoIsFor: [
      'Patients with asthma starting new medication',
      'Diabetes patients on new treatments',
      'Those starting high blood pressure medication',
      'COPD patients',
      'Patients on anticoagulant therapy',
    ],
    faqs: [
      {
        question: 'Is NMS free?',
        answer: "Yes, it's an NHS service.",
      },
      {
        question: 'How does it work?',
        answer: "You'll receive follow-up consultations after starting your medicine.",
      },
      {
        question: 'Do I need a referral?',
        answer: 'No—your pharmacist will invite you if eligible.',
      },
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
  },

  // Women's Health & Contraception
  {
    id: 'regular-contraception-pill',
    title: 'Pharmacy Contraception Service (Regular Pill)',
    category: 'womens-health',
    shortDescription: 'Free NHS service providing ongoing oral contraceptive pills without needing a GP appointment.',
    fullDescription: 'The NHS Pharmacy Contraception Service (PCS) allows eligible patients to access ongoing oral contraception directly from a trained pharmacist, without needing a GP appointment. At Wilberforce Pharmacy, eligible patients can start or continue combined oral contraceptive pills or progestogen-only pills. Our pharmacist will provide advice on correct use, what to do about missed pills, and managing any side effects. This service supports continuity of contraception without delays caused by GP appointment waiting times.',
    seoTitle: 'NHS Regular Contraceptive Pill Service in Hull | Wilberforce Pharmacy',
    metaDescription: 'Free NHS contraceptive pill service at Wilberforce Pharmacy Hull. Start or continue your regular oral contraception without a GP appointment.',
    whoIsFor: [
      'Adults seeking to start oral contraception',
      'Those wanting to continue their current contraceptive pill',
      'Patients seeking combined oral contraceptive pills',
      'Those seeking progestogen-only pills (mini pill)',
      'Patients who prefer a confidential, pharmacy-based service',
      'Those meeting NHS eligibility criteria',
    ],
    faqs: [
      {
        question: 'Is the Pharmacy Contraception Service free?',
        answer: 'Yes. Ongoing oral contraception is provided free of charge under the NHS for eligible patients.',
      },
      {
        question: 'What types of contraceptive pills are available?',
        answer: 'The pharmacist can provide combined oral contraceptive pills or progestogen-only pills (mini pill) depending on your needs and medical suitability.',
      },
      {
        question: 'Do I need an appointment?',
        answer: 'Often no — walk-ins are usually accepted, but availability may vary. You can ask in-pharmacy.',
      },
      {
        question: 'Is the service confidential?',
        answer: 'Yes. All contraception consultations are conducted in a private consultation room.',
      },
      {
        question: 'Will my GP be informed?',
        answer: 'Where required, your GP will be notified in line with NHS guidance.',
      },
      {
        question: 'What if I have missed a pill?',
        answer: 'The pharmacist can provide advice on what to do if you have missed pills, including whether additional protection is needed.',
      },
      {
        question: 'Can I get advice on side effects?',
        answer: 'Yes. The pharmacist can discuss potential side effects and help manage any concerns you may have.',
      },
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
  },
  {
    id: 'emergency-contraception',
    title: 'Oral Emergency Contraception (Morning After Pill)',
    category: 'womens-health',
    shortDescription: 'Free NHS emergency contraception available urgently following unprotected sex.',
    fullDescription: 'Oral emergency contraception (commonly known as the morning after pill or Plan B) can be provided under the NHS Pharmacy Contraception Service following a confidential pharmacist consultation. Emergency contraception may be needed after unprotected sex, missed contraceptive pills, condom failure (split or slipped), or vomiting/diarrhoea affecting pill absorption. Depending on timing and clinical suitability, the pharmacist may supply emergency contraception effective up to 3 days (72 hours) or up to 5 days (120 hours) after sex. The pharmacist will assess time since unprotected sex, current contraception use, medical history, medicines, body weight and other clinical factors.',
    seoTitle: 'NHS Morning After Pill in Hull | Emergency Contraception | Wilberforce Pharmacy',
    metaDescription: 'Free NHS emergency contraception (morning after pill) at Wilberforce Pharmacy Hull. Urgent confidential service without GP appointment.',
    whoIsFor: [
      'Those who have had unprotected sex',
      'People who have missed contraceptive pills',
      'Those who experienced condom failure (split or slipped)',
      'Patients affected by vomiting or diarrhoea while on the pill',
      'Anyone needing urgent emergency contraception',
      'Those meeting NHS eligibility criteria',
    ],
    faqs: [
      {
        question: 'Is emergency contraception free on the NHS?',
        answer: 'Yes. Oral emergency contraception is provided free of charge under the NHS for eligible patients.',
      },
      {
        question: 'How soon should I take emergency contraception?',
        answer: 'As soon as possible after unprotected sex. Effectiveness reduces over time, so earlier is better.',
      },
      {
        question: 'What types of emergency contraception are available?',
        answer: 'Depending on timing and clinical suitability, the pharmacist may supply emergency contraception effective up to 3 days (72 hours) or up to 5 days (120 hours) after sex.',
      },
      {
        question: 'What will the pharmacist assess?',
        answer: 'The pharmacist will assess time since unprotected sex, current contraception use, medical history, medicines, body weight and other clinical factors.',
      },
      {
        question: 'Do I need an appointment?',
        answer: 'Often no — walk-ins are usually accepted for emergency contraception, but availability may vary.',
      },
      {
        question: 'Is the service confidential?',
        answer: 'Yes. All emergency contraception consultations are conducted in a private consultation room.',
      },
      {
        question: 'Will my GP be informed?',
        answer: 'Where required, your GP will be notified in line with NHS guidance.',
      },
      {
        question: 'Can I get ongoing contraception after emergency contraception?',
        answer: 'Yes. The pharmacist can advise on starting or continuing regular contraception after emergency contraception to prevent future emergencies.',
      },
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
  },

  // Pharmacy First Conditions
  {
    id: 'pharmacy-first-earache',
    title: 'Earache Treatment',
    category: 'pharmacy-first',
    shortDescription: 'NHS assessment and treatment for mild to moderate earache.',
    fullDescription: 'Earache is pain in or around the ear and is commonly caused by outer ear infections (otitis externa), middle ear inflammation, or trapped fluid or wax irritation. Under Pharmacy First, the pharmacist can assess mild to moderate earache, identify signs of infection or inflammation, and provide treatment where appropriate.',
    seoTitle: 'Earache Treatment Without GP | NHS Pharmacy First Hull',
    metaDescription: 'Get NHS earache treatment at Wilberforce Pharmacy Hull. Fast assessment without GP appointment.',
    whoIsFor: [
      'People with ear pain or discomfort',
      'Those with tenderness around the ear',
      'Patients with mild hearing changes',
      'Those experiencing itching or fullness in the ear',
    ],
    faqs: [
      {
        question: 'Is treatment free?',
        answer: 'Yes, fully funded by the NHS for eligible patients.',
      },
      {
        question: 'Do I need an appointment?',
        answer: 'Often no — walk-ins are usually accepted.',
      },
      {
        question: 'When would I need to see a GP?',
        answer: 'Severe pain, discharge, fever, hearing loss, or symptoms in very young children may require GP review.',
      },
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
  },
  {
    id: 'pharmacy-first-sore-throat',
    title: 'Sore Throat Treatment',
    category: 'pharmacy-first',
    shortDescription: 'NHS assessment and treatment for sore throat symptoms.',
    fullDescription: 'A sore throat is often caused by viral infections but can sometimes be bacterial. Most cases improve without antibiotics. The pharmacist can assess your symptoms, including duration of illness, severity of pain, and presence of fever or swollen glands. If criteria are met, treatment may be provided without a GP appointment.',
    seoTitle: 'Sore Throat Treatment Without GP | NHS Pharmacy First Hull',
    metaDescription: 'Get NHS sore throat treatment at Wilberforce Pharmacy Hull. Fast assessment without GP appointment.',
    whoIsFor: [
      'People with pain or scratchiness when swallowing',
      'Those with red or swollen throat',
      'Patients with swollen glands in the neck',
      'Those with fever alongside throat symptoms',
    ],
    faqs: [
      {
        question: 'Is treatment free?',
        answer: 'Yes, fully funded by the NHS for eligible patients.',
      },
      {
        question: 'Do I need an appointment?',
        answer: 'Often no — walk-ins are usually accepted.',
      },
      {
        question: 'When would I need to see a GP?',
        answer: 'Difficulty breathing, inability to swallow, or symptoms lasting longer than expected.',
      },
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
  },
  {
    id: 'pharmacy-first-sinusitis',
    title: 'Sinusitis Treatment',
    category: 'pharmacy-first',
    shortDescription: 'NHS assessment and treatment for acute sinusitis.',
    fullDescription: 'Sinusitis is inflammation of the sinuses, often following a cold or flu. It causes pressure and pain around the face. Pharmacists can assess acute sinusitis, provide advice, and supply treatment when appropriate.',
    seoTitle: 'Sinusitis Treatment Without GP | NHS Pharmacy First Hull',
    metaDescription: 'Get NHS sinusitis treatment at Wilberforce Pharmacy Hull. Fast assessment without GP appointment.',
    whoIsFor: [
      'People with facial pain or pressure',
      'Those with blocked or runny nose',
      'Patients experiencing headaches',
      'Those with reduced sense of smell',
      'People with thick nasal discharge',
    ],
    faqs: [
      {
        question: 'Is treatment free?',
        answer: 'Yes, fully funded by the NHS for eligible patients.',
      },
      {
        question: 'Do I need an appointment?',
        answer: 'Often no — walk-ins are usually accepted.',
      },
      {
        question: 'When would I need to see a GP?',
        answer: 'Severe facial swelling, high fever, symptoms lasting longer than 10 days, or repeated episodes.',
      },
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
  },
  {
    id: 'pharmacy-first-insect-bites',
    title: 'Infected Insect Bites Treatment',
    category: 'pharmacy-first',
    shortDescription: 'NHS assessment and treatment for infected insect bites.',
    fullDescription: 'Most insect bites heal on their own, but infection can occur when bacteria enter the skin. The pharmacist can assess whether a bite shows signs of infection and provide treatment where appropriate under the NHS service.',
    seoTitle: 'Infected Insect Bite Treatment | NHS Pharmacy First Hull',
    metaDescription: 'Get NHS treatment for infected insect bites at Wilberforce Pharmacy Hull. Fast assessment without GP appointment.',
    whoIsFor: [
      'People with increasing redness or swelling around a bite',
      'Those with warmth around the bite area',
      'Patients experiencing pain or tenderness',
      'Those with pus or discharge from the bite',
    ],
    faqs: [
      {
        question: 'Is treatment free?',
        answer: 'Yes, fully funded by the NHS for eligible patients.',
      },
      {
        question: 'Do I need an appointment?',
        answer: 'Often no — walk-ins are usually accepted.',
      },
      {
        question: 'When would I need to see a GP?',
        answer: 'Spreading redness, fever, severe pain, or signs of an allergic reaction.',
      },
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
  },
  {
    id: 'pharmacy-first-uti',
    title: 'Urinary Tract Infection (UTI) Treatment',
    category: 'pharmacy-first',
    shortDescription: 'NHS assessment and treatment for UTIs in eligible women.',
    fullDescription: 'A urinary tract infection is a common bacterial infection affecting the bladder. Eligible women can receive assessment and treatment for uncomplicated UTIs directly from the pharmacist, without waiting for a GP appointment.',
    seoTitle: 'UTI Treatment Without GP | NHS Pharmacy First Hull',
    metaDescription: 'Get NHS UTI treatment for women at Wilberforce Pharmacy Hull. Fast assessment without GP appointment.',
    whoIsFor: [
      'Women with burning or stinging when passing urine',
      'Those needing to urinate more often',
      'Women with urgent need to pass urine',
      'Those with lower abdominal discomfort',
      'Women with cloudy or strong-smelling urine',
    ],
    faqs: [
      {
        question: 'Is treatment free?',
        answer: 'Yes, fully funded by the NHS for eligible patients.',
      },
      {
        question: 'Do I need an appointment?',
        answer: 'Often no — walk-ins are usually accepted.',
      },
      {
        question: 'When would I need to see a GP?',
        answer: 'Symptoms such as fever, back pain, pregnancy, or recurrent UTIs may require GP care.',
      },
      {
        question: 'Is this service available for men?',
        answer: 'The NHS Pharmacy First UTI service is currently for eligible women only. Men with UTI symptoms should see their GP.',
      },
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
  },
  {
    id: 'pharmacy-first-shingles',
    title: 'Shingles (Early Treatment)',
    category: 'pharmacy-first',
    shortDescription: 'NHS assessment and early treatment for shingles within 72 hours of rash.',
    fullDescription: 'Shingles is a viral infection caused by reactivation of the chickenpox virus. Early treatment is important to reduce pain and complications. Pharmacists can assess early shingles (usually within 72 hours of rash appearance) and provide treatment under the NHS where appropriate. Prompt treatment can reduce severity of symptoms, duration of illness, and risk of long-term nerve pain (post-herpetic neuralgia).',
    seoTitle: 'Shingles Treatment Without GP | NHS Pharmacy First Hull',
    metaDescription: 'Get early NHS shingles treatment at Wilberforce Pharmacy Hull. Fast assessment without GP appointment.',
    whoIsFor: [
      'People with pain, tingling or burning sensation on one side of the body',
      'Those with a rash developing into fluid-filled blisters',
      'Patients with sensitivity to touch',
      'Anyone with shingles symptoms within 72 hours of rash appearance',
    ],
    faqs: [
      {
        question: 'Is treatment free?',
        answer: 'Yes, fully funded by the NHS for eligible patients.',
      },
      {
        question: 'Why is early treatment important?',
        answer: 'Prompt treatment can reduce severity, duration, and risk of long-term nerve pain.',
      },
      {
        question: 'When would I need to see a GP urgently?',
        answer: 'Shingles affecting the face, eyes, or severe pain requires urgent medical attention.',
      },
    ],
    bookingUrl: 'https://healthclinicshull.setmore.com/',
  },
];

export const nhsGeneralFAQs: NHSServiceFAQ[] = [
  {
    question: 'Are NHS pharmacy services free?',
    answer: 'Yes. All services listed are fully funded by the NHS for eligible patients.',
  },
  {
    question: 'Do I need a GP referral?',
    answer: 'No. Many NHS pharmacy services allow you to self-present directly at the pharmacy.',
  },
  {
    question: 'Are consultations confidential?',
    answer: 'Yes. All NHS consultations are carried out in a private consultation room.',
  },
  {
    question: 'Will my GP be informed?',
    answer: 'Yes, where required. Communication is handled securely in line with NHS guidance.',
  },
  {
    question: 'Can I walk in?',
    answer: 'Many services accept walk-ins, but availability may vary. You can also ask our team for advice.',
  },
];
